import React, { useEffect, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import { timeAgo } from '../components/bible/rich';
import V2Shell from './V2Shell';

// Team notes: what the team knows right now, for Replai to weigh on every ticket
// (Geoff, 2026-10-15: "problem with Seletti BIC lamp, high delay, production problems").
const API = 'api/replai/v2/notes/';
const EMPTY = { text: '', applies_to: '', until: '', shareable: false };

const Editor = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState({ ...EMPTY, ...initial, until: initial?.until || '' });
  const field = 'rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-sm';
  return (
    <div className="space-y-3 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-4">
      <textarea value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} rows={3} autoFocus
        placeholder="e.g. Seletti BIC lamp: long delay at the brand, production problems. Expect new dates late November."
        className={`${field} w-full`} />
      <div className="flex flex-wrap items-center gap-3">
        <input value={f.applies_to} onChange={(e) => setF({ ...f, applies_to: e.target.value })}
          placeholder="About (brand or product, optional)" className={`${field} w-72`} />
        <label className="flex items-center gap-2 text-sm text-[var(--ink-2)]">Until
          <input type="date" value={f.until} onChange={(e) => setF({ ...f, until: e.target.value })} className={field} />
        </label>
        <label className="flex items-center gap-2 text-sm text-[var(--ink-2)]">
          <input type="checkbox" checked={f.shareable} onChange={(e) => setF({ ...f, shareable: e.target.checked })} />
          OK to tell customers (in general words)
        </label>
        <div className="ml-auto flex gap-2">
          {onCancel && <button type="button" onClick={onCancel} className="rounded-md px-3 py-2 text-sm text-[var(--ink-2)] hover:bg-[var(--line-2)]">Cancel</button>}
          <button type="button" disabled={!f.text.trim()} onClick={() => onSave({ ...f, until: f.until || null })}
            className="rounded-md bg-[var(--ink)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50">Save</button>
        </div>
      </div>
    </div>
  );
};

const NotesV2 = () => {
  const [notes, setNotes] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => axiosInstance.get(API).then(({ data }) => setNotes(data.notes || [])).catch(() => setNotes([]));
  useEffect(() => { load(); }, []);
  const save = (n, d) => (n ? axiosInstance.patch(`${API}${n.id}/`, d) : axiosInstance.post(API, d))
    .then(() => { setAdding(false); setEditing(null); load(); });
  const toggle = (n) => axiosInstance.patch(`${API}${n.id}/`, { active: !n.active }).then(load);
  const remove = (n) => window.confirm('Delete this note?') && axiosInstance.delete(`${API}${n.id}/`).then(load);
  const today = new Date().toISOString().slice(0, 10);
  return (
    <V2Shell>
      <div className="px-6 py-10 sm:px-10 xl:px-14">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[2.4rem] font-semibold leading-[1.1]">Team notes</h1>
            <p className="prose-body mt-2 max-w-3xl text-[var(--ink-2)]">
              What the team knows right now: a brand with production problems, a supplier delay, a known defect.
              Replai reads every active note on every ticket and mentions the relevant ones to you. Notes stay
              internal unless you tick "OK to tell customers". Policies belong in the Bible.
            </p>
          </div>
          {!adding && <button type="button" onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--ink)] px-4 py-2 font-medium text-white"><Plus size={16} /> New note</button>}
        </div>
        {adding && <div className="mt-6"><Editor onSave={(d) => save(null, d)} onCancel={() => setAdding(false)} /></div>}
        {notes == null ? <div className="mt-6 h-40 animate-pulse rounded-lg bg-[var(--line-2)]" /> : (
          <ul className="mt-6 space-y-3">
            {notes.map((n) => {
              const expired = n.until && n.until < today;
              const live = n.active && !expired;
              return editing === n.id ? <li key={n.id}><Editor initial={n} onSave={(d) => save(n, d)} onCancel={() => setEditing(null)} /></li> : (
                <li key={n.id} className={`rounded-lg border border-[var(--line)] bg-[var(--surface)] p-4 ${live ? '' : 'opacity-60'}`}>
                  <div className="flex items-start gap-3">
                    <p className="flex-1 whitespace-pre-wrap text-[0.95rem] leading-relaxed">{n.text}</p>
                    <button type="button" onClick={() => setEditing(n.id)} aria-label="Edit" className="rounded p-1 text-[var(--ink-3)] hover:bg-[var(--line-2)]"><Pencil size={15} /></button>
                    <button type="button" onClick={() => remove(n)} aria-label="Delete" className="rounded p-1 text-[var(--ink-3)] hover:bg-[var(--line-2)]"><Trash2 size={15} /></button>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[var(--ink-3)]">
                    <span className={`rounded-full px-2 py-0.5 font-medium ${live ? 'bg-green-100 text-green-800' : 'bg-[var(--line-2)] text-[var(--ink-2)]'}`}>
                      {expired ? 'Expired' : n.active ? 'Active' : 'Off'}</span>
                    {n.applies_to && <span className="rounded-full bg-[var(--line-2)] px-2 py-0.5">About: {n.applies_to}</span>}
                    <span className={`rounded-full px-2 py-0.5 ${n.shareable ? 'bg-[var(--ask-tint)] text-[var(--ask)]' : 'bg-[var(--line-2)]'}`}>
                      {n.shareable ? 'OK to tell customers' : 'Internal only'}</span>
                    {n.until && <span>until {n.until}</span>}
                    <span>· {n.author || 'team'}, {timeAgo(n.updated_at)}</span>
                    <button type="button" onClick={() => toggle(n)} className="ml-auto underline">{n.active ? 'Switch off' : 'Switch on'}</button>
                  </div>
                </li>
              );
            })}
            {!notes.length && !adding && <li className="rounded-lg border border-dashed border-[var(--line)] p-6 text-sm text-[var(--ink-3)]">No notes yet. Add the first one: what should Replai know this week?</li>}
          </ul>
        )}
      </div>
    </V2Shell>
  );
};

export default NotesV2;
